/* istanbul ignore file */
import { ContentStatus } from 'src/app/shared/enums/content-status';
import { IImage } from '../models/i-image';
import { IArticleContentDto } from './interfaces/i-article-content-dto';
import { IVideoMediaDto } from './interfaces/i-video-media-dto';
import { IDynamicData } from './interfaces/i-dynamic-data';
import { ContentType } from 'src/app/content-management/shared/enums/content-type';
import { IRepurposingDetail } from '../models/i-article-repurposing-detail';
import { IAssetClass } from '../models/i-asset-class';
import { IAuthor } from '../models/i-author';
import { ICompany } from '../models/i-company';
import { IContentList } from '../models/i-content-list';
import { IEtf } from '../models/i-etf';
import { IExternalLink } from '../models/i-external-link';
import { IFund } from '../models/i-fund';
import { IInvestmentTrust } from '../models/i-investment-trust';
import { IManager } from '../models/i-manager';
import { IPerson } from '../models/i-person';
import { ISector } from '../models/i-sector';
import { IShare } from '../models/i-share';
import { ISponsor } from '../models/i-sponsor';
import { ITag } from '../models/i-tag';
import { IRelatedContent } from '../models/i-related-content';
import { IPrivateMarketsFund } from '../models/i-private-markets-fund';
import { ITopic } from '../models/i-topic';
import { IContentListDto } from './interfaces/i-content-list-dto';
import { IImgMediaDto } from './interfaces/i-img-media-dto';
import { ISurtitle } from '../models/i-surtitle';

export class ArticleContentDto implements IArticleContentDto {
  public id!: string;
  public cas!: string;
  public contentId!: string;
  public headline!: string;
  public standfirst!: string;
  public body!: string;
  public bodyImages!: IImgMediaDto[];
  public bodyVideos!: IVideoMediaDto[];
  public deliveryTargetCodes: string[];
  public contentLists: IContentListDto[];
  public timezoneIdentifier: string;
  public mainImage: IImgMediaDto;
  public mainVideo: IVideoMediaDto;
  public repurposedFromArticleId: string;
  public overallRepurposingParentId: string;
  public repurposedArticles: IRepurposingDetail[];
  public repurposedFromArticles: IRepurposingDetail[];
  public authors: IAuthor[];
  public relatedManagers: IManager[];
  public relatedAssetClasses: IAssetClass[];
  public relatedSectors: ISector[];
  public relatedFunds: IFund[];
  public relatedEtfs: IEtf[];
  public relatedInvestmentTrusts: IInvestmentTrust[];
  public relatedShares: IShare[];
  public relatedPrivateMarketsFunds: IPrivateMarketsFund[];
  public relatedContents: IRelatedContent[];
  public externalLinks: IExternalLink[];
  public relatedCompanies: ICompany[];
  public relatedPeople: IPerson[];
  public surtitles: string[];
  public surtitlesDetailed: ISurtitle[];
  public topics: ITopic[];
  public sponsor: ISponsor;
  public tags: ITag[];
  public languageCode: string;
  public status!: ContentStatus;
  public lastUpdatedDate!: Date;
  public createdDate!: Date;
  public publishedDate!: Date;
  public presetPublishDate!: Date;
  public dynamicData!: IDynamicData;
  public isSponsored: boolean;
  public isSpiked: boolean;
  public isColumnistContent: boolean;
  public displayImageOnWebsite: boolean;
  public restrictToRegisteredUsers: boolean;
  public hideThisArticle: boolean;
  public notForGoogleNewsSitemaps: boolean;
  public notForNewsFeeds: boolean;
  public paywallPremiumContent: boolean;
  public notForCompetitors: boolean;
  public lastUpdatedByUser!: string;
  public isPublished: () => boolean;
  public isCurrentLiveVersion: () => boolean;
  constructor({
    id,
    cas,
    contentId,
    headline,
    standfirst,
    body,
    bodyImages,
    bodyVideos,
    deliveryTargetCodes,
    contentLists,
    timezoneIdentifier,
    mainImage,
    mainVideo,
    repurposedFromArticleId,
    overallRepurposingParentId,
    repurposedArticles,
    repurposedFromArticles,
    authors,
    relatedManagers,
    relatedAssetClasses,
    relatedSectors,
    relatedFunds,
    relatedEtfs,
    relatedInvestmentTrusts,
    relatedShares,
    relatedPrivateMarketsFunds,
    relatedContents,
    externalLinks,
    relatedCompanies,
    relatedPeople,
    surtitles,
    surtitlesDetailed,
    topics,
    sponsor,
    tags,
    languageCode,
    status,
    lastUpdatedDate,
    lastUpdatedByUser,
    createdDate,
    publishedDate,
    presetPublishDate,
    dynamicData,
    isSponsored,
    isSpiked,
    isColumnistContent,
    displayImageOnWebsite,
    restrictToRegisteredUsers,
    hideThisArticle,
    notForGoogleNewsSitemaps,
    notForNewsFeeds,
    paywallPremiumContent,
    notForCompetitors,
  }: IArticleContentDto) {
    this.id = id;
    this.cas = cas;
    this.contentId = contentId;
    this.headline = headline;
    this.standfirst = standfirst;
    this.body = body;
    this.bodyImages = bodyImages ?? [];
    this.bodyVideos = bodyVideos ?? [];
    this.deliveryTargetCodes = deliveryTargetCodes ?? [];
    this.contentLists = contentLists ?? [];
    this.timezoneIdentifier = timezoneIdentifier;
    this.mainImage = mainImage;
    this.mainVideo = mainVideo;
    this.repurposedFromArticleId = repurposedFromArticleId;
    this.overallRepurposingParentId = overallRepurposingParentId;
    this.repurposedArticles = repurposedArticles ?? [];
    this.repurposedFromArticles = repurposedFromArticles ?? [];
    this.authors = authors ?? [];
    this.relatedManagers = relatedManagers ?? [];
    this.relatedAssetClasses = relatedAssetClasses ?? [];
    this.relatedSectors = relatedSectors ?? [];
    this.relatedFunds = relatedFunds ?? [];
    this.relatedEtfs = relatedEtfs ?? [];
    this.relatedInvestmentTrusts = relatedInvestmentTrusts ?? [];
    this.relatedShares = relatedShares ?? [];
    this.relatedPrivateMarketsFunds = relatedPrivateMarketsFunds ?? [];
    this.relatedContents = relatedContents ?? [];
    this.externalLinks = externalLinks ?? [];
    this.relatedCompanies = relatedCompanies ?? [];
    this.relatedPeople = relatedPeople ?? [];
    this.surtitles = surtitles ?? [];
    this.surtitlesDetailed = surtitlesDetailed ?? [];
    this.topics = topics ?? [];
    this.sponsor = sponsor;
    this.tags = tags ?? [];
    this.languageCode = languageCode;
    this.status = status;
    this.lastUpdatedDate = lastUpdatedDate;
    this.createdDate = createdDate;
    this.publishedDate = publishedDate;
    this.presetPublishDate = presetPublishDate;
    this.dynamicData = { ...dynamicData };
    this.lastUpdatedByUser = lastUpdatedByUser;
    this.isSponsored = isSponsored;
    this.isSpiked = isSpiked;
    this.isColumnistContent = isColumnistContent;
    this.displayImageOnWebsite = displayImageOnWebsite;
    this.restrictToRegisteredUsers = restrictToRegisteredUsers;
    this.hideThisArticle = hideThisArticle;
    this.notForGoogleNewsSitemaps = notForGoogleNewsSitemaps;
    this.notForNewsFeeds = notForNewsFeeds;
    this.paywallPremiumContent = paywallPremiumContent;
    this.notForCompetitors = notForCompetitors;
    this.isPublished = () => this.status === ContentStatus.PUBLISHED;
    this.isCurrentLiveVersion = () =>
      this.id === this.dynamicData?.latestPublishedVersionId;
  }
}
